// filepath: apps/web/src/features/cabinet/RequiredUserParameters/queries.ts
//
// FR-UI-CABINET-3 — TanStack Query hooks for the user-self-service
// required user-parameters surface.
//
// All hooks include tenantSlug in the cache key as the 2nd segment
// (INV-1, FR-UI-CORE-1 pattern; mirrors observability queries.ts).
// Switching tenants does NOT leak the prior tenant's required
// parameters — the cache key changes, the query refetches.
//
// Security invariants:
//   - INV-1: the SPA renders exactly what the server returns. No
//     client-side filtering of the field set; the server is the
//     authoritative source of "what is required" (FR-UI-CORE-3 +
//     FR-UI-CORE-5).
//   - INV-2: field-level RBAC stripping is server-side. The SPA
//     renders whatever fields the server includes.
//   - INV-5: the patch body may carry sensitive values (tax_id,
//     etc.). The query layer forwards the body verbatim — no
//     logging of values on the wire, no localStorage/sessionStorage
//     persistence (caught by apps/web/src/definitions/staticGuards.test.ts
//     AC-5).
//
// 5-error-state contract (FR-UI-CORE-10): the typed ApiError is
// surfaced and classified via classifyRequiredParametersError in
// types.ts.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";
import type {
  PatchRequiredParametersRequest,
  RequiredParametersResponse,
} from "./types";

/** Use the canonical SPA API base path. Tests override via MSW. */
const DEFAULT_API_BASE = "/api";

export const requiredParametersKeys = {
  all: ["required-user-parameters"] as const,
  detail: (tenantSlug: string, subject: string) =>
    [...requiredParametersKeys.all, tenantSlug, subject] as const,
};

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

async function fetchRequiredParameters(
  apiBase: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _signal: AbortSignal,
): Promise<RequiredParametersResponse> {
  let response: Response;
  try {
    // ISS-377 round 2 (B1.2): this call site was the canonical
    // raw-fetch bypass. The round-1 regex did not match the
    // `fetch(`${apiBase}/me/required-parameters`, ...)` shape
    // (template literal with `${apiBase}` interpolation). Migrating
    // to apiFetch attaches the OIDC bearer via the auto-bearer
    // accessor and routes any 401 through the silent-refresh path.
    // The AbortSignal is intentionally not forwarded (see the
    // legacy comment below) — vitest issue 7250 still applies. design-adherence-ignore
    response = await apiFetch(`${apiBase}/me/required-parameters`, {
      method: "GET",
      init: {
        headers: { Accept: "application/json" },
        // NOTE: TanStack Query passes an AbortSignal for cancellation on
        // unmount. We deliberately do NOT forward it because the
        // vitest 2 + jsdom environment polyfills AbortSignal in a way
        // that fails undici's `instanceof AbortSignal` check (see the
        // vitest issue tracker — vitest#7250), so forwarding it throws
        // `RequestInit: Expected signal (...) to be an instance of
        // AbortSignal` before MSW can intercept. Production browsers
        // provide a real AbortSignal so this works there; if we ever
        // need unmount-cancellation we will route through a local
        // AbortController (`new AbortController().signal`) that we own.
        signal: undefined,
      },
    });
  } catch (networkErr) {
    throw new ApiError(
      networkErr instanceof Error ? networkErr.message : "network error",
      0,
      "network_error",
    );
  }
  if (!response.ok) {
    const body = await safeReadJson(response);
    const code =
      (body as { code?: string } | null)?.code ?? `http_${response.status}`;
    if (response.status === 409) {
      // FR-ENT-8 / FR-UI-CORE-6 — preserve the server's
      // current_row_version in the typed conflict envelope so the
      // stale-version surface can render it.
      const { HttpConflictError } = await import("@/definitions/types");
      const current = (body as { current_row_version?: unknown } | null)
        ?.current_row_version;
      throw new HttpConflictError(
        (body as { error?: string } | null)?.error ?? response.statusText,
        response.status,
        code,
        typeof current === "number" ? current : null,
        null,
      );
    }
    throw new ApiError(
      (body as { error?: string } | null)?.error ?? response.statusText,
      response.status,
      code,
    );
  }
  return (await response.json()) as RequiredParametersResponse;
}

// readSubject is the canonical subject accessor for the user. It
// reads from the OIDC user profile (the JWT `sub` claim) — the
// same source the existing cabinet chrome reads from
// (apps/web/src/features/cabinet/chrome/UserMenu.tsx:pickIdentity).
// No localStorage / sessionStorage access (caught by
// apps/web/src/definitions/staticGuards.test.ts AC-5).
export function readSubjectFromAuth(authUserProfile: unknown): string {
  if (typeof authUserProfile !== "object" || authUserProfile === null)
    return "";
  const sub = (authUserProfile as { sub?: unknown }).sub;
  if (typeof sub === "string" && sub !== "") return sub;
  return "";
}

/**
 * useRequiredParameters — GET /api/me/required-parameters.
 *
 * Returns the typed RequiredParametersResponse envelope (mirrors
 * internal/userprofile.RequiredParameterResponse). The endpoint is
 * tenant-scoped server-side via the kernel resolver middleware; the
 * SPA trusts the wire contract and renders exactly what the server
 * returns (INV-2 / FR-AUTH-7).
 *
 * The hook reads the OIDC user subject via useAuth() to satisfy
 * INV-1 cache-key tenant-scoping — the cache key is keyed on
 * (tenantSlug, subject) so cross-tenant cache entries can never
 * leak.
 */
export function useRequiredParameters(apiBase = DEFAULT_API_BASE) {
  const { tenantSlug } = useTenant();
  const auth = useAuth();
  const subject = readSubjectFromAuth(auth.user?.profile);
  return useQuery<RequiredParametersResponse, ApiError>({
    queryKey: requiredParametersKeys.detail(tenantSlug, subject),
    queryFn: ({ signal }) => fetchRequiredParameters(apiBase, signal),
    enabled: Boolean(tenantSlug) && Boolean(subject),
  });
}

/**
 * usePatchRequiredParameters — PATCH /api/me/required-parameters.
 *
 * Forwards the user's value updates with optimistic concurrency
 * (row_version per FR-ENT-8 / INV-7) and the FR-NFR-3 idempotency-key
 * header. On 409 the hook surfaces the conflict envelope so the
 * page can render the FR-UI-CORE-6 conflict-resolution flow.
 */
export function usePatchRequiredParameters(apiBase = DEFAULT_API_BASE) {
  const { tenantSlug } = useTenant();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const subject = readSubjectFromAuth(auth.user?.profile);
  return useMutation<
    RequiredParametersResponse,
    ApiError,
    PatchRequiredParametersRequest
  >({
    mutationFn: async (request) => {
      // ISS-377 round 2 (B1.2 + B3): the round-1 raw fetch
      // bypassed both the auto-bearer accessor AND the
      // idempotency-fallback hardening. Migrating to apiFetch
      // attaches the bearer via the accessor; `idempotencyKey:
      // true` lets apiFetch mint a fresh v4 UUID with the
      // round-2 fallback (getRandomValues) — no more hand-rolled
      // `cryptoRandomUUID` shim with `Math.random` fallback.
      let response: Response;
      try {
        response = await apiFetch(`${apiBase}/me/required-parameters`, {
          method: "PATCH",
          init: {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(request),
          },
          idempotencyKey: true,
        });
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }
      if (!response.ok) {
        const body = await safeReadJson(response);
        const code =
          (body as { code?: string } | null)?.code ?? `http_${response.status}`;
        if (response.status === 409) {
          const { HttpConflictError } = await import("@/definitions/types");
          const current = (body as { current_row_version?: unknown } | null)
            ?.current_row_version;
          throw new HttpConflictError(
            (body as { error?: string } | null)?.error ?? response.statusText,
            response.status,
            code,
            typeof current === "number" ? current : null,
            null,
          );
        }
        throw new ApiError(
          (body as { error?: string } | null)?.error ?? response.statusText,
          response.status,
          code,
        );
      }
      return (await response.json()) as RequiredParametersResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: requiredParametersKeys.detail(tenantSlug, subject),
      });
    },
  });
}
